/**
 * review-application.ts
 *
 * School Portal — Review Application modal behaviour.
 *
 * Responsibilities:
 * - Shows or hides the "Reason for Not Accepting" textarea based on which
 *   radio button is selected. The textarea group (`[data-review-reason-group]`)
 *   carries Bootstrap's `d-none` utility class by default; the script removes or
 *   adds the class in response to the `change` event on the radio inputs.
 *
 * The module is a no-op when `[data-review-application]` is absent from the DOM
 * (i.e. on all pages other than the application details page when the Review
 * button is rendered).
 */

/** Returns the modal host element — the element with `[data-review-application]`. */
function getModalEl(): HTMLElement | null {
  return document.querySelector('[data-review-application]') as HTMLElement | null;
}

/**
 * Updates the visibility of the reason group based on the currently selected decision.
 * Clears the textarea value when "Accepted" is chosen to avoid sending stale text.
 * All selectors are scoped to `modalEl` to avoid collisions with other forms on the page.
 */
function syncReasonGroupVisibility(modalEl: HTMLElement): void {
  const reasonGroup = modalEl.querySelector<HTMLElement>('[data-review-reason-group]');
  const textarea = modalEl.querySelector<HTMLTextAreaElement>('#StatusReason');
  const valueGroup = modalEl.querySelector<HTMLElement>('[data-review-value-group]');
  const valueInput = modalEl.querySelector<HTMLInputElement>('#approve-value-group input');
  if (!reasonGroup) return;

  const checkedRadio = modalEl.querySelector<HTMLInputElement>(
    'input[name="Decision"]:checked'
  );

  if (checkedRadio?.dataset.reviewDecision === 'not-accepted') {
    reasonGroup.classList.remove('d-none');
    if (valueGroup) {
      valueGroup.classList.add('d-none');
      if (valueInput) valueInput.value = '';
    }
  } else if (checkedRadio?.dataset.reviewDecision === 'accepted') {
    reasonGroup.classList.add('d-none');
    if (textarea) textarea.value = '';
    if (valueGroup) valueGroup.classList.remove('d-none');
  } else {
    reasonGroup.classList.add('d-none');
    if (textarea) textarea.value = '';
    if (valueGroup) valueGroup.classList.add('d-none');
    if (valueInput) valueInput.value = '';
  }
}

/**
 * Attaches `change` listeners to the Decision radio group inside the review modal.
 */
function bindDecisionRadios(modalEl: HTMLElement): void {
  const radios = modalEl.querySelectorAll<HTMLInputElement>(
    'input[name="Decision"]'
  );
  radios.forEach((radio) => {
    radio.addEventListener('change', () => syncReasonGroupVisibility(modalEl));
  });
  // Sync once immediately to set correct initial state.
  syncReasonGroupVisibility(modalEl);
}

/**
 * Initialises the review-application module.
 * Searches the DOM for `[data-review-application]` and wires up all behaviours.
 * Safe to call on every page — exits immediately when the element is absent.
 */
export function initReviewApplication(): void {
  const modalEl = getModalEl();
  if (!modalEl) return;

  bindDecisionRadios(modalEl);
}
